import type { BlockDefinitionMeta } from '@tsagko/label-contracts'
import { Trans, useTranslation } from 'react-i18next'
import {
  CollapsedRail,
  PanelCollapseToggle,
  panelBodyId,
  usePanelCollapsed,
} from '../ui/CollapsibleSection'

/** Panel id for the collapse state + its localStorage key (#189). */
const PANEL_ID = 'blockPalette'

type Flavour = 'published' | 'draft'

/** Payload serialised into the drag dataTransfer */
export interface PaletteDragPayload {
  blockId: string
  flavour: Flavour
}

export const PALETTE_DRAG_TYPE = 'application/x-label-block'

interface BlockPaletteProps {
  items: BlockDefinitionMeta[]
  activePick?: { id: string; flavour: Flavour }
  loading?: boolean
  onPick: (id: string, flavour: Flavour) => void
  onClearPick: () => void
}

export function BlockPalette({ items, activePick, loading, onPick, onClearPick }: BlockPaletteProps) {
  const { t } = useTranslation()
  const { collapsed, toggle, toggleRef } = usePanelCollapsed(PANEL_ID)
  const title = t('templateDesigner.panels.blockPalette')

  // Flatten each meta into 0–2 rows: published (if latestVersion > 0) + draft (if hasDraft).
  const rows = items.flatMap(item => {
    const out: Array<{ key: string; item: BlockDefinitionMeta; flavour: Flavour }> = []
    if (item.latestVersion > 0) {
      out.push({ key: `${item.id}::published`, item, flavour: 'published' })
    }
    if (item.hasDraft) {
      out.push({ key: `${item.id}::draft`, item, flavour: 'draft' })
    }
    return out
  })

  // Collapsed (#189): the whole aside is replaced by a 36px rail, so its
  // width goes to the canvas column (which is `flex-1` in the same row).
  if (collapsed) {
    return (
      <CollapsedRail
        panelId={PANEL_ID}
        label={title}
        onExpand={toggle}
        side="left"
        toggleRef={toggleRef}
      />
    )
  }

  return (
    // `overflow-hidden` on the aside, `overflow-auto` on the list below
    // (#190): now that the designer row has a bounded height the aside would
    // otherwise become a second scroller wrapped around the list's own one,
    // giving the palette two nested scrollbars. The header stays pinned and
    // the list is the single scroll region.
    // `aria-controls` names the aside itself: collapsing swaps the WHOLE
    // panel for the rail, header included, so the aside is the region the
    // toggle shows and hides.
    <aside
      id={panelBodyId(PANEL_ID)}
      data-testid="block-palette"
      className="flex w-56 shrink-0 flex-col overflow-hidden border-r border-stone-200 bg-white dark:border-stone-800 dark:bg-stone-900"
    >
      <div className="border-b border-stone-200 px-4 py-3 dark:border-stone-800">
        <div className="flex items-center gap-1.5">
          <h3 className="text-[11px] font-semibold uppercase tracking-wide text-stone-500">
            {title}
          </h3>
          <PanelCollapseToggle
            className="ml-auto"
            collapsed={false}
            onToggle={toggle}
            panelLabel={title}
            controls={panelBodyId(PANEL_ID)}
            testId={`${PANEL_ID}-toggle`}
            expandedChevron="left"
            buttonRef={toggleRef}
          />
        </div>
        {activePick && (
          <div className="mt-2 rounded border border-fuchsia-400 bg-fuchsia-50 px-2 py-1.5 text-[11px] text-fuchsia-800 dark:border-fuchsia-600 dark:bg-fuchsia-950 dark:text-fuchsia-200">
            {/* <Trans> and not a plain t(): the picked block's id stays bold
                inside the sentence, and each language is free to move it. The
                `<strong>` in the message needs no `components` map — it is one
                of react-i18next's default `transKeepBasicHtmlNodesFor` tags. */}
            <Trans
              i18nKey="templateDesigner.palette.dropHint"
              values={{
                block:
                  activePick.flavour === 'draft'
                    ? t('templateDesigner.palette.draftPick', { id: activePick.id })
                    : activePick.id,
              }}
            />
            <button type="button" onClick={onClearPick} className="ml-1 underline">
              {t('templateDesigner.palette.cancelPick')}
            </button>
          </div>
        )}
      </div>
      <div data-testid="block-palette-list" className="flex-1 overflow-auto p-2">
        {loading && (
          <p className="text-xs text-stone-400 px-2">{t('templateDesigner.palette.loading')}</p>
        )}
        {!loading && rows.length === 0 && (
          <p className="text-xs text-stone-400 px-2">{t('templateDesigner.palette.empty')}</p>
        )}
        <ul className="space-y-1.5">
          {rows.map(row => {
            const isActive = activePick?.id === row.item.id && activePick?.flavour === row.flavour
            return (
              <li key={row.key}>
                <button
                  type="button"
                  data-testid="palette-block"
                  data-block-id={row.item.id}
                  data-flavour={row.flavour}
                  draggable
                  onDragStart={e => {
                    const payload: PaletteDragPayload = { blockId: row.item.id, flavour: row.flavour }
                    e.dataTransfer.setData(PALETTE_DRAG_TYPE, JSON.stringify(payload))
                    e.dataTransfer.effectAllowed = 'copy'
                  }}
                  onClick={() => onPick(row.item.id, row.flavour)}
                  className={[
                    'flex w-full items-start gap-2 rounded border px-2 py-1.5 text-left transition',
                    isActive
                      ? 'border-fuchsia-500 bg-fuchsia-50 dark:bg-fuchsia-950'
                      : row.flavour === 'draft'
                        ? 'border-amber-300 bg-amber-50 hover:border-amber-500 dark:border-amber-700 dark:bg-amber-950'
                        : 'border-stone-200 bg-stone-50 hover:border-stone-400 dark:border-stone-800 dark:bg-stone-950',
                  ].join(' ')}
                >
                  <span
                    className={[
                      'rounded px-1.5 py-0.5 text-[9px] uppercase tracking-wide',
                      row.flavour === 'draft'
                        ? 'bg-amber-200 text-amber-900 dark:bg-amber-700 dark:text-amber-100'
                        : row.item.tier === 'platform'
                          ? 'bg-sky-100 text-sky-700 dark:bg-sky-950 dark:text-sky-300'
                          : 'bg-stone-100 text-stone-700 dark:bg-stone-800 dark:text-stone-300',
                    ].join(' ')}
                  >
                    {row.flavour === 'draft'
                      ? t('templateDesigner.palette.draftBadge')
                      : t(`tier.${row.item.tier}`)}
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-xs font-semibold">{row.item.name}</div>
                    <div className="truncate text-[10px] text-stone-500">
                      {row.flavour === 'draft'
                        ? t('templateDesigner.palette.draftVersion')
                        : `v${row.item.latestVersion}`}
                      {/* `defaultValue`: BlockDefinition.category is a free
                          `z.string()` in the contract, so a legacy or
                          hand-authored value outside CATEGORIES must fall back
                          to itself instead of printing the raw i18n key. */}
                      {row.item.category &&
                        ` · ${t(`category.${row.item.category}`, { defaultValue: row.item.category })}`}
                    </div>
                  </div>
                </button>
              </li>
            )
          })}
        </ul>
      </div>
    </aside>
  )
}
